export class OnboardingApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message)
    this.name = 'OnboardingApiError'
  }
}

/** PUT /draft was rejected: someone else saved first (or setup finished). */
export class DraftConflictError extends OnboardingApiError {
  constructor(
    message: string,
    /** The server's current revision, when it sent one. */
    public serverRevision: number | null,
  ) {
    super(409, message)
    this.name = 'DraftConflictError'
  }
}

/** POST /complete was rejected with business-rule problems. */
export class DraftValidationError extends OnboardingApiError {
  constructor(
    message: string,
    public problems: CommitProblem[],
  ) {
    super(422, message)
    this.name = 'DraftValidationError'
  }
}

export type Sector =
  | 'carehomes'
  | 'dentistry'
  | 'healthcare'
  | 'homecare'
  | 'other'

export type EmployeeRole = 'admin' | 'manager' | 'worker'

export interface DraftBuilding {
  tempId: string
  name: string
  address: string
  isLogicalGroup: boolean
}

export interface DraftArea {
  tempId: string
  buildingTempId: string
  name: string
}

export interface DraftSkill {
  tempId: string
  name: string
}

export interface DraftEmployee {
  tempId: string
  name: string
  email: string
  role: EmployeeRole
  skillTempIds: string[]
}

export interface OnboardingDraft {
  sector: Sector | null
  buildings: DraftBuilding[]
  areas: DraftArea[]
  skills: DraftSkill[]
  employees: DraftEmployee[]
  skippedStepIds: string[]
}

export function emptyDraft(): OnboardingDraft {
  return {
    sector: null,
    buildings: [],
    areas: [],
    skills: [],
    employees: [],
    skippedStepIds: [],
  }
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error' | 'conflict'
export type CommitStatus = 'idle' | 'committing' | 'done' | 'error'

export interface CommitProblem {
  entity: 'building' | 'area' | 'skill'
  tempId: string | null
  field: string
  code: 
    | 'required'
    | 'duplicate'
    | 'already_exists'
  message: string
}

export const stepForEntity: Record<CommitProblem['entity'], string> = {
  building: 'buildings',
  area: 'areas',
  skill: 'skills',
}

export interface AreaSuggestion {
  id: string
  label: string
}